import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: 'Home',
      breadcrumb: 'Home'
    }
  },
  {
    path: '/diseases',
    name: 'DiseasesDirectory',
    component: () => import(/* webpackChunkName: "diseases-directory" */ '@/components/diseases/core/DiseasesDirectoryView.vue'),
    meta: {
      title: 'Diseases Directory',
      breadcrumb: 'Diseases'
    }
  },
  {
    path: '/genes',
    name: 'GenesDirectory',
    component: () => import(/* webpackChunkName: "genes-directory" */ '@/components/genes/core/GenesDirectoryView.vue'),
    meta: {
      title: 'Genes Directory',
      breadcrumb: 'Genes'
    }
  },
  {
    path: '/patients',
    name: 'Patients',
    component: () => import('@/views/PatientsPage.vue'),
    meta: {
      title: 'Patient Management',
      breadcrumb: 'Patients'
    }
  },
  {
    path: '/patients/:patientId',
    name: 'PatientDetail',
    component: () => import('@/views/PatientDetailView.vue'),
    meta: {
      title: 'Patient Details',
      breadcrumb: 'Patient Details'
    }
  },
  {
    path: '/text-to-json',
    name: 'TextToJson',
    component: () => import('@/components/text-to-json/core/TextToJsonView.vue'),
    meta: {
      title: 'Text to JSON',
      breadcrumb: 'Text to JSON'
    }
  },
  {
    path: '/json-to-csv',
    name: 'JsonToCsv',
    component: () => import('@/views/ComingSoonView.vue'),
    meta: {
      title: 'JSON to CSV',
      breadcrumb: 'JSON to CSV',
      comingSoon: true
    }
  },
  {
    path: '/prs-chart-builder/:projectId?',
    name: 'PrsChartBuilder',
    component: () => import('@/views/PrsChartBuilderView.vue'),
    meta: {
      title: 'PRS Chart Builder',
      breadcrumb: 'PRS Chart Builder'
    }
  },
  {
    path: '/report-themer',
    name: 'ReportThemePreview',
    component: () => import('@/views/ReportThemerView.vue'),
    meta: {
      title: 'Report Themer',
      breadcrumb: 'Report Themer'
    }
  },
  {
    path: '/ai-retrieval-lab',
    name: 'AiRetrievalLab',
    component: () => import('@/views/AiRetrievalLabView.vue'),
    meta: {
      title: 'AI Retrieval Lab',
      breadcrumb: 'AI Retrieval Lab'
    }
  },
  {
    path: '/journeys',
    name: 'JourneyLibrary',
    component: () => import('@/views/JourneyLibraryView.vue'),
    meta: {
      title: 'Journey Visualiser',
      breadcrumb: 'Journeys'
    }
  },
  {
    path: '/journeys/new',
    name: 'JourneyNew',
    component: () => import('@/views/JourneyEditorView.vue'),
    meta: {
      title: 'New Journey',
      breadcrumb: 'New Journey'
    }
  },
  {
    path: '/journeys/:projectId',
    name: 'JourneyViewer',
    component: () => import('@/views/JourneyViewerView.vue'),
    meta: {
      title: 'Journey Viewer',
      breadcrumb: 'Journey Viewer',
      standaloneViewer: true
    }
  },
  {
    path: '/journeys/:projectId/edit',
    name: 'JourneyEditor',
    component: () => import('@/views/JourneyEditorView.vue'),
    meta: {
      title: 'Journey Editor',
      breadcrumb: 'Journey Editor'
    }
  },
  {
    path: '/markr',
    name: 'ImageAnnotation',
    component: () => import('@/components/markr/core/HomePage.vue'),
    meta: {
      title: 'Markr - Image Annotation',
      breadcrumb: 'Markr'
    }
  },
  {
    path: '/markr/patient/:patientId',
    name: 'PatientWorkspace',
    component: () => import('@/components/markr/core/MarkrView.vue'),
    meta: {
      title: 'Markr - Patient Workspace',
      breadcrumb: 'Markr'
    }
  },
  {
    // Use base-aware path; final URL will include BASE_URL
    path: '/timeline-builder/:projectId?',
    name: 'timeline-builder',
    component: () => import('@/components/timeline-builder/TimelineBuilderApp.vue'),
    meta: {
      title: 'Timeline Builder',
      breadcrumb: 'Timeline Builder'
    }
  },
  {
    // The 23S ID is the readable canonical GCCM URL; the legacy identifier route below remains for existing links.
    path: '/gccm/id/:twentythreesid',
    name: 'gccm-editor-id',
    component: () => import('@/components/gccm/GccmEditorApp.vue'),
    meta: {
      title: 'GCCM Editor',
      breadcrumb: 'GCCM Editor'
    }
  },
  {
    path: '/gccm/:patientIdentifier?',
    name: 'gccm-editor',
    component: () => import('@/components/gccm/GccmEditorApp.vue'),
    meta: {
      title: 'GCCM Editor',
      breadcrumb: 'GCCM Editor'
    }
  },
  {
    path: '/inheritance-patterns',
    name: 'inheritance-patterns',
    component: () => import('@/components/inheritance-pattern/InheritancePatternView.vue'),
    meta: {
      title: 'Inheritance Patterns',
      breadcrumb: 'Inheritance Patterns'
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: {
      title: 'Settings',
      breadcrumb: 'Settings'
    },
    children: [
      {
        path: '',
        name: 'SettingsRedirect',
        redirect: '/settings/categories'
      },
      {
        path: 'categories',
        name: 'SettingsCategories',
        component: () => import('@/components/settings/NestedCategoryManagementPage.vue'),
        meta: {
          title: 'Manage Categories',
          breadcrumb: 'Categories'
        }
      },
      {
        path: 'colours',
        name: 'SettingsColours',
        component: () => import('@/views/settings/ColoursManagerView.vue'),
        meta: {
          title: 'Manage Colours',
          breadcrumb: 'Colours'
        }
      }
      ,
      {
        path: 'icons',
        name: 'SettingsIcons',
        component: () => import('@/components/settings/IconDirectoryPage.vue'),
        meta: {
          title: 'Icon Library',
          breadcrumb: 'Icons'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/'
  }
]

const router = createRouter({
  // Respect the app base so refreshes work under subpaths (e.g., /tools/)
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

export default router
